import { Widget } from '@/components/widget';
import { ObjectsTable } from '@/components/objects-table';
import { Modal } from '@/components/modal';
import { modalText } from '@/config/modal';
import { useGetUserVault } from '../../hooks/use-get-user-vault';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useEmitter } from '@/hooks/use-emitter';
import { useScreenId } from '@/hooks/use-screen-id';
import { useTabId } from '@/hooks/use-tab-id';
import { useKeycloak } from '@react-keycloak/web';
import { useVaultItemDelete } from '@/hooks/use-vault-item-delete';

interface VaultListWidgetProps {
  id: string;
  hideable?: boolean;
  defaultIsVisible?: boolean;
  showEdit?: boolean;
}

export function VaultListWidget({
  id,
  defaultIsVisible = true,
  hideable = false,
  showEdit = false,
}: VaultListWidgetProps) {
  const { keycloak } = useKeycloak();
  const { data, isLoading } = useGetUserVault();
  const [isVisible, setIsVisible] = useState(defaultIsVisible);
  const [deleteUuid, setDeleteUuid] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const vaultItemDeleteMutation = useVaultItemDelete();
  const screenId = useScreenId();
  const tabId = useTabId();
  const emit = useEmitter({
    id,
    listenersPrefix: `${tabId}/${screenId}`,
  });

  function handleDragData(uuid: string, label?: string) {
    const vault = transformedVaults.find((v) => v.uuid === uuid);
    return JSON.stringify({
      type: 'object/insert',
      value: [{ uuid, type: 'vault', label, ...(vault?.app ? { app: vault.app } : {}) }],
    });
  }

  const transformedVaults = Array.isArray(data)
    ? data.map((vault) => ({
        uuid: vault.value,
        name: vault.label,
        dataClassUuid: vault.dataClassUuid,
        app: vault.app,
      }))
    : [];

  function handleDeleteClick(uuid: string) {
    setDeleteError(null);
    setDeleteUuid(uuid);
  }

  function handleDeleteCancel() {
    setDeleteUuid(null);
    setDeleteError(null);
  }

  function handleDeleteConfirm() {
    if (!deleteUuid) {
      return;
    }

    setDeleteError(null);

    vaultItemDeleteMutation.mutate(
      { key: deleteUuid },
      {
        onSuccess: () => {
          setDeleteUuid(null);
        },
        onError: (error: unknown) => {
          setDeleteError(error instanceof Error ? error.message : 'Failed to delete vault item');
        },
      }
    );
  }

  function handleEdit(uuid: string) {
    const vault = transformedVaults.find((v) => v.uuid === uuid);
    emit({
      type: 'ObjectCollection',
      data: [
        {
          container: 'vault',
          uuid,
          root: 'vault',
          dataClassUuid: vault?.dataClassUuid,
          ...(vault?.app ? { app: vault.app } : {}),
        },
      ],
    });
  }

  return (
    <Widget
      id={id}
      title="Vaults"
      hideable={hideable}
      initialVisible={isVisible}
      onVisibilityChange={setIsVisible}
    >
      <ObjectsTable
        data={transformedVaults || []}
        isLoading={isLoading}
        setDragData={handleDragData}
        onEdit={showEdit ? handleEdit : undefined}
        onDelete={showEdit ? handleDeleteClick : undefined}
      />
      {deleteUuid && (
        <Modal
          onCancel={handleDeleteCancel}
          onSubmit={handleDeleteConfirm}
          loading={vaultItemDeleteMutation.isLoading}
          title={modalText.deleteVaultItemTitle}
          message={modalText.deleteVaultItemMessage}
          actionName={modalText.deleteVaultItemActionName}
          offsetSidebar={true}
          error={deleteError ?? undefined}
        />
      )}
    </Widget>
  );
}
